import { useCallback } from 'react';
import { useStoreApi } from '@xyflow/react';

/**
 * Hook that provides a function to programmatically select nodes in the canvas.
 *
 * This hook wraps React Flow's `addSelectedNodes` method from the store API.
 *
 * @see {@link https://reactflow.dev/api-reference/hooks/use-store-api React Flow useStoreApi documentation}
 *
 * @returns A callback function that accepts an array of node IDs to select
 *
 * @example
 * ```tsx
 * const selectNodes = useSelectNodes();
 *
 * // Select single node
 * selectNodes(['node-1']);
 *
 * // Select multiple nodes
 * selectNodes(['node-1', 'node-2', 'node-3']);
 * ```
 */
export const useSelectNodes = () => {
  const store = useStoreApi();

  const selectNodes = useCallback(
    (nodeIds: string[]) => {
      const { addSelectedNodes } = store.getState();
      addSelectedNodes(nodeIds);
    },
    [store],
  );

  return selectNodes;
};
